import type { tPendingFollowUp } from './UpcomingSendsBar.types';

/**
 * INT-1500 U12: the attribute that marks a scheduled card in the thread, so
 * "Ver" can find the card of the next send. EventBubble sets it on the
 * card's wrapper; nothing else reads it.
 */
export const SCHEDULED_CARD_ANCHOR_ATTRIBUTE = 'data-follow-up-event-id';

/** The props that mark a scheduled card's wrapper as the card of `chatEventId`. */
export function scheduledCardAnchorProps(
  chatEventId: string,
): Record<typeof SCHEDULED_CARD_ANCHOR_ATTRIBUTE, string> {
  return { [SCHEDULED_CARD_ANCHOR_ATTRIBUTE]: chatEventId };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readId(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function readCampaignId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
  return null;
}

function readItem(value: unknown): tPendingFollowUp | null {
  if (!isRecord(value)) return null;

  const id = readId(value.id);
  const chatEventId = readId(value.chat_event_id);
  const triggerDate =
    typeof value.trigger_date === 'string' ? value.trigger_date : null;
  if (!id || !chatEventId || !triggerDate) return null;
  if (Number.isNaN(new Date(triggerDate).getTime())) return null;

  const content = isRecord(value.content) ? value.content : {};

  return {
    id,
    chatEventId,
    triggerDate,
    isWapiTemplate: value.is_wapi_template === true,
    campaignId: readCampaignId(value.campaign_id),
    createdBy: typeof value.created_by === 'string' ? value.created_by : '',
    text: typeof content.text === 'string' ? content.text : '',
  };
}

/**
 * INT-1500 U12 (contract C7): reads the body of bff-web's
 * GET /chat/:cid/follow-ups/pending. This is the only place that knows its
 * shape.
 *
 * messaging answers a bare JSON array (messaging f881ab52) of `{ id,
 * chat_event_id, trigger_date, is_wapi_template, campaign_id, created_by,
 * content: { text } }` in snake_case, ordered by trigger date then id, and
 * bff-web relays it byte for byte (it turns any 2xx that is not an array into
 * a 424, bff-web f7fa01a). An envelope with the array in `data` is read too.
 * Anything else reads as "nothing pending": the bar hides instead of guessing.
 *
 * An item without an id, a chat event id or a readable trigger date is
 * dropped, a card listed twice counts once, and the result is ordered by
 * trigger date, soonest first; the sort is stable, so sends due at the same
 * time keep the server's order (by id).
 */
export function parsePendingFollowUps(raw: unknown): tPendingFollowUp[] {
  const list = Array.isArray(raw)
    ? raw
    : isRecord(raw) && Array.isArray(raw.data)
      ? raw.data
      : [];

  const seen = new Set<string>();
  const items: tPendingFollowUp[] = [];
  for (const entry of list) {
    const item = readItem(entry);
    if (!item || seen.has(item.chatEventId)) continue;
    seen.add(item.chatEventId);
    items.push(item);
  }

  return items.sort(
    (a, b) =>
      new Date(a.triggerDate).getTime() - new Date(b.triggerDate).getTime(),
  );
}

/**
 * The scheduled card of a chat event when it is on screen (it may not be: the
 * thread pages its history, and a card whose message was already sent gives
 * way to it). Compares the attribute's value instead of building a selector,
 * so no id ever needs escaping.
 */
export function findScheduledCard(
  chatEventId: string,
  root: ParentNode = document,
): HTMLElement | null {
  const cards = root.querySelectorAll<HTMLElement>(
    `[${SCHEDULED_CARD_ANCHOR_ATTRIBUTE}]`,
  );
  for (const card of Array.from(cards)) {
    if (card.getAttribute(SCHEDULED_CARD_ANCHOR_ATTRIBUTE) === chatEventId) {
      return card;
    }
  }
  return null;
}
